import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Package, ShoppingBag, EyeOff, PackageX, Percent, Boxes, Search, X } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';

const API_BASE = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
const PAGE = 200;

const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');
const fmtTL = (v) => (v == null ? '—' : `₺${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`);
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

// Sale status of a product on the web store. "Open for sale" = visible on the storefront
// AND has stock. ikas returns VISIBLE / HIDDEN / PASSIVE per sales channel; NOT_LISTED =
// the product was never added to the storefront channel.
const STATUS = {
  open:    { label: 'Open for sale',       short: 'Open',          bar: 'bg-emerald-500', text: 'text-emerald-600' },
  nostock: { label: 'Visible, no stock',   short: 'No stock',      bar: 'bg-amber-400',   text: 'text-amber-600' },
  hidden:  { label: 'Hidden / passive',    short: 'Hidden/passive', bar: 'bg-gray-400',    text: 'text-gray-500' },
};
const STATUS_KEYS = ['open', 'nostock', 'hidden'];

function statusOf(p) {
  if (p.status == null) return null;                  // snapshot without status columns
  if (p.status !== 'VISIBLE') return 'hidden';
  return p.stock > 0 ? 'open' : 'nostock';
}

function emptyAgg(key) {
  return { key, products: 0, open: 0, nostock: 0, hidden: 0, discounted: 0, fullPrice: 0,
    stock: 0, value: 0, discSum: 0, discN: 0 };
}
function addTo(a, p) {
  a.products += 1;
  if (p._st) a[p._st] += 1;
  if (p.discountPrice != null) {
    a.discounted += 1;
    a.discSum += (1 - p.discountPrice / p.sellPrice) * 100;
    a.discN += 1;
  } else a.fullPrice += 1;
  a.stock += p.stock;
  a.value += p.retailValue;
}

export default function IkasInventory() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || (typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : '') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');          // '' | open | nostock | hidden
  const [disc, setDisc] = useState('');              // '' | yes | no
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [catSort, setCatSort] = useState('products');
  const [brandSort, setBrandSort] = useState('products');

  const fetchData = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/ikas/inventory`, { credentials: 'include', headers: { 'X-Brand-ID': brandId } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setLimit(PAGE); }, [brand, category, status, disc, search]);

  const products = useMemo(
    () => (data?.products || []).map((p) => ({ ...p, _st: statusOf(p), brand: p.brand || '(no brand)' })),
    [data]
  );
  const hasStatus = !!data?.hasStatus;

  const matches = useCallback((p, skip) => {
    if (skip !== 'brand' && brand && p.brand !== brand) return false;
    if (skip !== 'category' && category && !(category === '(no category)' ? !p.categories.length : p.categories.includes(category))) return false;
    if (skip !== 'status' && status && p._st !== status) return false;
    if (skip !== 'disc' && disc && (disc === 'yes') !== (p.discountPrice != null)) return false;
    return true;
  }, [brand, category, status, disc]);

  // KPI scope = all filters except status/discount, so the cards show the split within the selection.
  const kpi = useMemo(() => {
    const a = emptyAgg('all');
    for (const p of products) if (matches(p, 'status') && matches(p, 'disc')) addTo(a, p);
    return a;
  }, [products, matches]);

  const byBrand = useMemo(() => {
    const m = new Map();
    for (const p of products) {
      if (!matches(p, 'brand')) continue;
      if (!m.has(p.brand)) m.set(p.brand, emptyAgg(p.brand));
      addTo(m.get(p.brand), p);
    }
    return [...m.values()].sort((x, y) => y[brandSort] - x[brandSort] || x.key.localeCompare(y.key));
  }, [products, matches, brandSort]);

  const byCategory = useMemo(() => {
    const m = new Map();
    for (const p of products) {
      if (!matches(p, 'category')) continue;
      const cats = p.categories.length ? p.categories : ['(no category)'];
      for (const c of cats) {
        if (!m.has(c)) m.set(c, emptyAgg(c));
        addTo(m.get(c), p);
      }
    }
    return [...m.values()].sort((x, y) => y[catSort] - x[catSort] || x.key.localeCompare(y.key));
  }, [products, matches, catSort]);

  const list = useMemo(() => {
    let r = products.filter((p) => matches(p));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter((p) => (p.name || '').toLowerCase().includes(q));
    }
    return r.sort((a, b) => b.stock - a.stock);
  }, [products, matches, search]);

  if (loading && !data) {
    return (
      <div className="p-6 max-w-[1320px] mx-auto">
        <Card className="rounded-2xl border border-gray-100 shadow-sm p-10 flex items-center justify-center text-gray-500">
          <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Loading…
        </Card>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 max-w-[1320px] mx-auto">
        <Card className="rounded-2xl border border-red-100 shadow-sm p-8 text-red-600">
          Error: {error}
          <button onClick={fetchData} className="ml-3 underline">Try again</button>
        </Card>
      </div>
    );
  }

  const filters = [
    brand && { k: 'brand', label: `Brand: ${brand}`, clear: () => setBrand('') },
    category && { k: 'category', label: `Category: ${category}`, clear: () => setCategory('') },
    status && { k: 'status', label: STATUS[status].label, clear: () => setStatus('') },
    disc && { k: 'disc', label: disc === 'yes' ? 'On discount' : 'Full price', clear: () => setDisc('') },
  ].filter(Boolean);

  return (
    <div className="p-6 space-y-6 max-w-[1320px] mx-auto">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        <div className="p-6 bg-white">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                <Boxes className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
                <p className="text-sm text-gray-500 mt-1 max-w-3xl">
                  Every product in the ikas catalog by brand and category: how many are on discount and how
                  many are open for sale (visible on the web store and in stock).
                  <span className="text-gray-400"> · Snapshot {data?.snapshotDate || '—'} (daily, 06:00)</span>
                </p>
              </div>
            </div>
            <button
              type="button" onClick={fetchData} title="Refresh"
              className="flex items-center justify-center w-10 h-10 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 shrink-0"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {!hasStatus && (
            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 px-4 py-2.5 text-sm text-amber-800">
              Sale status isn&apos;t in this snapshot yet — it is collected from the next daily snapshot on.
              Brand, category and discount figures are already complete.
            </div>
          )}

          <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat icon={Package} color="text-indigo-600 bg-indigo-50" label="Products" value={fmtNum(kpi.products)}
              sub={`${fmtNum(kpi.stock)} units in stock`} />
            {hasStatus && STATUS_KEYS.map((k) => (
              <Stat
                key={k} icon={k === 'open' ? ShoppingBag : k === 'nostock' ? PackageX : EyeOff}
                color={k === 'open' ? 'text-emerald-600 bg-emerald-50' : k === 'nostock' ? 'text-amber-600 bg-amber-50' : 'text-gray-500 bg-gray-100'}
                label={STATUS[k].label} value={fmtNum(kpi[k])} valueColor={STATUS[k].text}
                sub={`${pct(kpi[k], kpi.products)}% of products`}
                active={status === k} onClick={() => setStatus(status === k ? '' : k)}
              />
            ))}
            <Stat icon={Percent} color="text-red-600 bg-red-50" label="On discount" value={fmtNum(kpi.discounted)}
              valueColor="text-red-600"
              sub={`${pct(kpi.discounted, kpi.products)}% · avg ${kpi.discN ? Math.round(kpi.discSum / kpi.discN) : 0}% off`}
              active={disc === 'yes'} onClick={() => setDisc(disc === 'yes' ? '' : 'yes')} />
            <Stat icon={Package} color="text-gray-600 bg-gray-100" label="Full price" value={fmtNum(kpi.fullPrice)}
              sub={`${pct(kpi.fullPrice, kpi.products)}% of products`}
              active={disc === 'no'} onClick={() => setDisc(disc === 'no' ? '' : 'no')} />
          </div>

          {hasStatus && kpi.products > 0 && (
            <div className="mt-4">
              <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100">
                {STATUS_KEYS.map((k) => (
                  <div key={k} className={STATUS[k].bar} style={{ width: `${(kpi[k] / kpi.products) * 100}%` }} title={`${STATUS[k].label}: ${kpi[k]}`} />
                ))}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-4 text-xs text-gray-500">
                {STATUS_KEYS.map((k) => (
                  <span key={k} className="inline-flex items-center gap-1.5">
                    <span className={`w-2.5 h-2.5 rounded-sm ${STATUS[k].bar}`} /> {STATUS[k].label} {pct(kpi[k], kpi.products)}%
                  </span>
                ))}
              </div>
            </div>
          )}

          {filters.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-gray-500">Filtered by:</span>
              {filters.map((f) => (
                <button key={f.k} onClick={f.clear}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 hover:bg-indigo-100">
                  {f.label} <X className="w-3.5 h-3.5" />
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>

      <div className="space-y-6">
        <BreakdownTable
          title="By brand" rows={byBrand} hasStatus={hasStatus}
          selected={brand} onSelect={(k) => setBrand(brand === k ? '' : k)}
          sort={brandSort} setSort={setBrandSort}
        />
        <BreakdownTable
          title="By category" rows={byCategory} hasStatus={hasStatus}
          note="A product can sit in more than one category, so the rows add up to more than the product count."
          selected={category} onSelect={(k) => setCategory(category === k ? '' : k)}
          sort={catSort} setSort={setCatSort}
        />
      </div>

      <Card className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-gray-700">Products</span>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product…"
              className="border border-gray-300 rounded-lg pl-9 pr-3 h-9 text-sm w-64 bg-white" />
          </div>
          <span className="text-sm text-gray-400 ml-auto tabular-nums">{fmtNum(list.length)} products</span>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-white border-b border-gray-100">
              <tr className="text-gray-500">
                <th className="text-left px-5 py-2.5 font-medium">Product</th>
                <th className="text-left px-4 py-2.5 font-medium">Category</th>
                {hasStatus && <th className="text-left px-4 py-2.5 font-medium">Status</th>}
                <th className="text-right px-4 py-2.5 font-medium">Stock</th>
                <th className="text-right px-4 py-2.5 font-medium">Price</th>
                <th className="text-right px-5 py-2.5 font-medium">Discounted price</th>
              </tr>
            </thead>
            <tbody>
              {list.slice(0, limit).map((p) => (
                <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-5 py-2">
                    <div className="font-medium text-gray-900 leading-tight">{p.name}</div>
                    <div className="text-xs text-gray-400">{p.brand}</div>
                  </td>
                  <td className="px-4 py-2 text-gray-600 text-xs max-w-[220px]">{p.categories.join(', ') || '—'}</td>
                  {hasStatus && (
                    <td className={`px-4 py-2 text-xs font-medium whitespace-nowrap ${p._st ? STATUS[p._st].text : 'text-gray-400'}`}>
                      {p._st ? STATUS[p._st].short : '—'}
                      {p.status && p.status !== 'VISIBLE' && <span className="text-gray-400 font-normal"> · {p.status.toLowerCase().replace('_', ' ')}</span>}
                    </td>
                  )}
                  <td className={`px-4 py-2 text-right tabular-nums ${p.stock > 0 ? 'text-gray-800' : 'text-gray-300'}`}>
                    {fmtNum(p.stock)}
                    {p.inStockVariants != null && p.variants > 1 && (
                      <div className="text-[11px] text-gray-400">{p.inStockVariants}/{p.variants} variants</div>
                    )}
                  </td>
                  <td className={`px-4 py-2 text-right tabular-nums ${p.discountPrice != null ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                    {fmtTL(p.sellPrice)}
                  </td>
                  <td className="px-5 py-2 text-right tabular-nums">
                    {p.discountPrice != null ? (
                      <span className="text-red-600 font-medium">
                        {fmtTL(p.discountPrice)}
                        <span className="text-[11px] font-normal text-red-400"> −{Math.round((1 - p.discountPrice / p.sellPrice) * 100)}%</span>
                      </span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.length === 0 && <div className="p-8 text-center text-gray-400">No products.</div>}
          {list.length > limit && (
            <div className="p-4 text-center">
              <button onClick={() => setLimit((l) => l + PAGE)} className="text-sm text-indigo-600 font-medium hover:underline">
                Show {fmtNum(Math.min(PAGE, list.length - limit))} more
              </button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function BreakdownTable({ title, rows, hasStatus, selected, onSelect, note, sort, setSort }) {
  const cols = [
    { k: 'products', label: 'Products' },
    ...(hasStatus ? STATUS_KEYS.map((k) => ({ k, label: STATUS[k].short, cls: STATUS[k].text })) : []),
    { k: 'discounted', label: 'Discount', cls: 'text-red-600' },
    { k: 'fullPrice', label: 'Full price' },
    { k: 'stock', label: 'Units' },
  ];
  return (
    <Card className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
      <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
        <span className="text-sm font-semibold text-gray-700">{title}</span>
        <span className="text-sm text-gray-400"> · click a row to filter · {rows.length} rows</span>
        {note && <div className="text-xs text-gray-400 mt-0.5">{note}</div>}
      </div>
      <div className="overflow-auto max-h-[420px]">
        <table className="min-w-full text-sm">
          <thead className="bg-white sticky top-0 z-10 border-b border-gray-100">
            <tr className="text-gray-500">
              <th className="text-left px-5 py-2.5 font-medium">{title.replace('By ', '').replace(/^./, (c) => c.toUpperCase())}</th>
              {cols.map((c) => (
                <th key={c.k} onClick={() => setSort(c.k)}
                  className={`text-right px-3 py-2.5 font-medium cursor-pointer whitespace-nowrap hover:text-gray-900 ${c.cls || ''} ${sort === c.k ? 'underline underline-offset-4' : ''}`}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} onClick={() => onSelect(r.key)}
                className={`border-b border-gray-50 cursor-pointer ${selected === r.key ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}>
                <td className="px-5 py-2 font-medium text-gray-900">
                  {r.key}
                  {hasStatus && r.products > 0 && (
                    <div className="mt-1 flex h-1 w-28 rounded-full overflow-hidden bg-gray-100">
                      {STATUS_KEYS.map((k) => <div key={k} className={STATUS[k].bar} style={{ width: `${(r[k] / r.products) * 100}%` }} />)}
                    </div>
                  )}
                </td>
                {cols.map((c) => (
                  <td key={c.k} className={`px-3 py-2 text-right tabular-nums ${c.k === 'products' ? 'font-semibold text-gray-900' : r[c.k] ? (c.cls || 'text-gray-700') : 'text-gray-300'}`}>
                    {fmtNum(r[c.k])}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={cols.length + 1} className="px-5 py-6 text-center text-gray-400">No rows.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function Stat({ icon: Icon, color, label, value, sub, valueColor = 'text-gray-900', active, onClick }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined} onClick={onClick}
      className={`text-left rounded-xl border p-3 flex items-center gap-3 ${active ? 'border-indigo-300 ring-1 ring-indigo-200 bg-indigo-50/40' : 'border-gray-100 bg-white'} ${onClick ? 'hover:border-gray-300' : ''}`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-gray-500 truncate">{label}</div>
        <div className={`text-lg font-bold tabular-nums truncate ${valueColor}`}>{value}</div>
        {sub && <div className="text-[11px] text-gray-400 truncate">{sub}</div>}
      </div>
    </Tag>
  );
}
